import { useState } from 'react';

import {
    PASOS_MOVIL,
    claveDelPaso,
    crearTManual,
    imagenDelPaso,
    type ClaveManual,
    type Idioma,
    type PasoManual,
} from '@haus/core';

import { Cabecera, Contenido, Pantalla } from '../componentes/estructura.js';
import { useApp } from '../estado/app.js';
import css from './manual.module.css';

/**
 * El manual, dentro de la app.
 *
 * Solo la mitad del móvil. El panel enseña las dos —quien está en la oficina
 * atiende el teléfono de quien está en un piso— pero al revés no: a quien está
 * limpiando no le sirve de nada leer cómo se exporta un CSV, y son doce
 * pantallas más de texto en un teléfono.
 *
 * ### Las imágenes y la cobertura
 *
 * Van FUERA del precaché del service worker (`globIgnores` en
 * `vite.config.ts`). Si entraran, cada instalación se bajaría todas las
 * capturas de los tres idiomas por la línea doméstica del NAS antes de poder
 * empezar a trabajar, y son para leerlas una vez. Con una regla de caché en
 * tiempo de ejecución se guardan al verlas, así que a partir de la primera
 * lectura el manual funciona sin cobertura.
 *
 * La primera vez sin red no hay imagen, y se dice: un hueco gris sin
 * explicación se lee como que la aplicación está rota, que es justo lo que un
 * manual no puede transmitir.
 */
export function PantallaManual() {
    const { idioma, atras } = useApp();
    const tm = crearTManual(idioma);

    return (
        <Pantalla>
            <Cabecera titulo={tm('titulo')} alVolver={atras} />
            <Contenido>
                <p className={css.intro}>{tm('seccion_movil_aqui')}</p>

                {PASOS_MOVIL.map((p, i) => (
                    <Paso key={p.id} paso={p} n={i + 1} tm={tm} idioma={idioma} />
                ))}
            </Contenido>
        </Pantalla>
    );
}

function Paso({
    paso,
    n,
    tm,
    idioma,
}: {
    paso: PasoManual;
    n: number;
    tm: ReturnType<typeof crearTManual>;
    idioma: Idioma;
}) {
    const [rota, setRota] = useState(false);
    const clave = (que: 'titulo' | 'cuerpo'): ClaveManual =>
        claveDelPaso('mo', paso, que) as ClaveManual;

    return (
        <section className={css.paso}>
            <span className="etiqueta-mono">{tm('paso', { n })}</span>
            <h2 className={css.titulo}>{tm(clave('titulo'))}</h2>
            <p className={css.cuerpo}>{tm(clave('cuerpo'))}</p>

            {rota ? (
                <p className={css.sinImagen}>{tm('imagen_no')}</p>
            ) : (
                <img
                    className={css.captura}
                    src={imagenDelPaso('/manual/', idioma, paso)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={() => setRota(true)}
                />
            )}
        </section>
    );
}
